/**
 * Assignment Modal Component
 * Allows admins to assign GPS assessments to leader teams
 */

import { useState } from 'react';
import { X, Users, Calendar, CheckCircle, AlertCircle, UserPlus, Trash2 } from 'lucide-react';
import { assignmentAPI } from '../../services/gpsAssignmentAPI';

const AssignmentModal = ({ assessment, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Search Leader, 2: Add Ad-hoc (optional), 3: Confirm
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [teamSize, setTeamSize] = useState(null);
  const [adhocParticipants, setAdhocParticipants] = useState([]);
  const [adhocSearchQuery, setAdhocSearchQuery] = useState('');
  const [adhocSearchResults, setAdhocSearchResults] = useState([]);
  const [searchingAdhoc, setSearchingAdhoc] = useState(false);
  const [showAdhocDropdown, setShowAdhocDropdown] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [searching, setSearching] = useState(false);
  const [loadingTeamSize, setLoadingTeamSize] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Debounced search function
  const handleSearchInput = async (query) => {
    setSearchQuery(query);
    setError(null);

    if (query.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setSearching(true);

    try {
      const response = await fetch(
        `https://jmaffrrt2j.execute-api.us-west-2.amazonaws.com/prod/api/search-users?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to search users');
      }

      const data = await response.json();
      setSearchResults(data.users || []);
      setShowDropdown(true);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search users. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Select a leader from search results
  const handleSelectLeader = async (leader) => {
    setSelectedLeader(leader);
    setSearchQuery(leader.name);
    setShowDropdown(false);
    setLoadingTeamSize(true);
    setError(null);

    try {
      // Fetch team size for selected leader
      const response = await fetch(
        `https://jmaffrrt2j.execute-api.us-west-2.amazonaws.com/prod/api/team-size?email=${encodeURIComponent(leader.email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch team information');
      }

      const data = await response.json();
      setTeamSize(data.teamSize || 0);
      setStep(2); // Move to ad-hoc participants step
    } catch (err) {
      console.error('Team size error:', err);
      setError('Failed to fetch team information. Please try again.');
    } finally {
      setLoadingTeamSize(false);
    }
  };

  // Search for ad-hoc participants
  const handleAdhocSearchInput = async (query) => {
    setAdhocSearchQuery(query);

    if (query.length < 2) {
      setAdhocSearchResults([]);
      setShowAdhocDropdown(false);
      return;
    }

    setSearchingAdhoc(true);

    try {
      const response = await fetch(
        `https://jmaffrrt2j.execute-api.us-west-2.amazonaws.com/prod/api/search-users?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to search users');
      }

      const data = await response.json();
      // Filter out already added participants and the leader
      const filtered = (data.users || []).filter(user => 
        user.email !== selectedLeader.email &&
        !adhocParticipants.some(p => p.email === user.email)
      );
      setAdhocSearchResults(filtered);
      setShowAdhocDropdown(true);
    } catch (err) {
      console.error('Ad-hoc search error:', err);
      setAdhocSearchResults([]);
    } finally {
      setSearchingAdhoc(false);
    }
  };

  // Add an ad-hoc participant
  const handleAddAdhocParticipant = (user) => {
    setAdhocParticipants([...adhocParticipants, {
      email: user.email,
      name: user.name,
      title: user.title,
      department: user.department,
      employeeId: user.employeeId
    }]);
    setAdhocSearchQuery('');
    setAdhocSearchResults([]);
    setShowAdhocDropdown(false);
  };

  // Remove an ad-hoc participant
  const handleRemoveAdhocParticipant = (email) => {
    setAdhocParticipants(adhocParticipants.filter(p => p.email !== email));
  };

  const handleAssign = async () => {
    setAssigning(true);
    setError(null);

    try {
      const result = await assignmentAPI.assignToTeam(
        assessment.AssessmentId,
        selectedLeader.email,
        `${startDate}T00:00:00Z`,
        adhocParticipants.length > 0 ? adhocParticipants : undefined
      );

      console.log('Assignment created:', result);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      onClose();
    } catch (err) {
      console.error('Assignment error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to assign assessment');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Assign Assessment to Team</h2>
            <p className="modal-subtitle">
              {assessment.SiteName} - {assessment.Year}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="form-group">
                <label className="form-label">Search for Leader</label>
                <p className="form-help">
                  Start typing a name or email to search for a leader
                </p>
                <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Type name or email..."
                        value={searchQuery}
                        onChange={(e) => handleSearchInput(e.target.value)}
                        onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                        disabled={loadingTeamSize}
                        style={{ paddingRight: '2.5rem' }}
                      />
                      {searching && (
                        <div style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}>
                          <div className="spinner-small"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Autocomplete Dropdown */}
                  {showDropdown && searchResults.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {searchResults.map((user) => (
                        <div
                          key={user.email}
                          className="autocomplete-item"
                          onClick={() => handleSelectLeader(user)}
                        >
                          <div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>{user.name}</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{user.email}</div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                              {user.title} • {user.department}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showDropdown && searchResults.length === 0 && searchQuery.length >= 2 && !searching && (
                    <div className="autocomplete-dropdown">
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                        No users found matching "{searchQuery}"
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '1.5rem' }}>
                <Users size={20} style={{ color: '#6366f1' }} />
                <div>
                  <strong>What happens next?</strong>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                    The system will fetch the leader's full team hierarchy from EntraID and create 7 weekly pillar assessments for each team member.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && selectedLeader && (
            <div>
              <div className="success-box" style={{ marginBottom: '1.5rem' }}>
                <CheckCircle size={20} style={{ color: '#10b981' }} />
                <div>
                  <strong>Leader Selected</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
                    {selectedLeader.name} ({selectedLeader.email})
                  </p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                    {selectedLeader.title} • {selectedLeader.department}
                  </p>
                  {teamSize !== null && (
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', fontWeight: '600', color: '#10b981' }}>
                      <Users size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                      Team Size: {teamSize} {teamSize === 1 ? 'person' : 'people'}
                    </p>
                  )}
                </div>
              </div>

              {/* Ad-hoc Participants Section */}
              <div className="form-group">
                <label className="form-label">
                  <UserPlus size={18} style={{ marginRight: '0.5rem' }} />
                  Add Individual Participants (Optional)
                </label>
                <p className="form-help">
                  Add team members who aren't in the leader's organization
                </p>

                {/* Search Input */}
                <div style={{ position: 'relative', marginTop: '0.75rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search by name or email..."
                    value={adhocSearchQuery}
                    onChange={(e) => handleAdhocSearchInput(e.target.value)}
                    onFocus={() => adhocSearchResults.length > 0 && setShowAdhocDropdown(true)}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  {searchingAdhoc && (
                    <div style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}>
                      <div className="spinner-small"></div>
                    </div>
                  )}

                  {/* Search Results Dropdown */}
                  {showAdhocDropdown && adhocSearchResults.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {adhocSearchResults.map((user) => (
                        <div
                          key={user.email}
                          className="autocomplete-item"
                          onClick={() => handleAddAdhocParticipant(user)}
                        >
                          <div>
                            <div style={{ fontWeight: '600', color: '#111827' }}>{user.name}</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{user.email}</div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                              {user.title} • {user.department}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Added Participants List */}
                {adhocParticipants.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                      Added ({adhocParticipants.length}):
                    </div>
                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                      {adhocParticipants.map((participant) => (
                        <div
                          key={participant.email}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.75rem',
                            background: '#f3e8ff',
                            border: '1px solid #d8b4fe',
                            borderRadius: '0.5rem'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                              {participant.name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {participant.email}
                            </div>
                            {participant.title && (
                              <div style={{ fontSize: '0.75rem', color: '#7c3aed', marginTop: '0.25rem' }}>
                                {participant.title}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveAdhocParticipant(participant.email)}
                            style={{
                              padding: '0.5rem',
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Remove participant"
                          >
                            <Trash2 size={16} color="#dc2626" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Calendar size={18} style={{ marginRight: '0.5rem' }} />
                  Start Date
                </label>
                <p className="form-help">
                  Pillar 1 will become active on this date
                </p>
                <input
                  type="date"
                  className="form-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ marginTop: '0.5rem' }}
                />
              </div>

              <div className="info-box" style={{ marginTop: '1.5rem' }}>
                <div>
                  <strong>Assignment Summary</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    <li>{(teamSize || 0) + adhocParticipants.length} total participants ({teamSize || 0} org + {adhocParticipants.length} ad-hoc)</li>
                    <li>7 pillars will be created (one per week)</li>
                    <li>Team members will see only the current active pillar</li>
                    <li>Pillars rotate automatically every 7 days</li>
                    <li>Cycle repeats until you close the assessment</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {step === 1 && (
            <button className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
          )}
          
          {step === 2 && (
            <>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setStep(1);
                  setSelectedLeader(null);
                  setTeamSize(null);
                  setAdhocParticipants([]);
                  setAdhocSearchQuery('');
                  setAdhocSearchResults([]);
                  setSearchQuery('');
                  setSearchResults([]);
                  setError(null);
                }}
                disabled={assigning}
              >
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAssign}
                disabled={assigning}
              >
                {assigning ? 'Assigning...' : `Assign to ${(teamSize || 0) + adhocParticipants.length} Participants`}
              </button>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: white;
          border-radius: 0.75rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0.25rem 0 0 0;
        }

        .modal-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 0.25rem;
          border-radius: 0.375rem;
          transition: all 0.2s;
        }

        .modal-close:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        .info-box {
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          border-radius: 0.5rem;
          font-size: 0.875rem;
        }

        .success-box {
          display: flex;
          gap: 0.75rem;
          padding: 1rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 0.5rem;
          font-size: 0.875rem;
        }

        .alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          border-radius: 0.5rem;
          font-size: 0.875rem;
        }

        .alert-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .form-help {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0.25rem 0 0 0;
        }

        .autocomplete-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          max-height: 300px;
          overflow-y: auto;
          z-index: 10;
          margin-top: 0.25rem;
        }

        .autocomplete-item {
          padding: 0.75rem 1rem;
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
          transition: background 0.15s;
        }

        .autocomplete-item:last-child {
          border-bottom: none;
        }

        .autocomplete-item:hover {
          background: #f9fafb;
        }

        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid #e5e7eb;
          border-top-color: #6366f1;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AssignmentModal;
